import Debug from 'debug';
import { EventEmitter } from 'events';

import { Environment } from '../../types/config';
import { Nullable } from '../../types/utility';
import { environment } from '../../environment';
import { createTokenFromSerializedToken, Token } from '../models/Token';
import { User } from '../models/User';
import { trackByDefault } from '../lib/host';
import { DialogName } from './DialogService';

export type SessionId = {
  id: string;
  last: number;
  environment: Environment;
};

export type AppSettings = BaseSettings & {
  sessionId: SessionId;
  bixelSendingPool: string;
  token: Token;
  user: User;
  trial: any; // TODO: define this
  version: string;
  livefireFilters?: Record<string, boolean>;
  trackAll: boolean;
  filterTracking: boolean;
  loginState: number;
};

export type SendingPoolSettings = BaseSettings & {
  host: string;
  hostUrl: string;
};

export type VideoStatsSettings = BaseSettings & {
  hasShownSendAndTrackInfo: boolean;
  hasDismissedSendAndTrackInfo: boolean;
};

type UISettings = BaseSettings & {
  showTrialBannerAfter: number;
  dialog: DialogName | null;
  videoTitles: string[] | null;
  videoIds: string[] | null;
};

export type AppSettingsService = SettingsService<AppSettings>;
export type SendingPoolSettingsService = SettingsService<SendingPoolSettings>;
export type VideoStatsSettingsService = SettingsService<VideoStatsSettings>;
export type UISettingsService = SettingsService<UISettings>;

type BaseSettings = {
  timestamp: number;
  environment: Environment;
};

// list of cached initialized services, also serves as a key for typing methods.
const services: {
  bombbomb?: AppSettingsService;
  sendingPool?: SendingPoolSettingsService;
  videoStats?: VideoStatsSettingsService;
  ui?: UISettingsService;
} = {};

/**
 * Gets and initializes a SettingsService object for the requested settings type.
 * @arg name: The name of the service to initialize.
 * @arg storage: The storage object to use for storing settings locally.
 * @arg roaming: The roaming storage object to store settings remotely.
 * @returns A customized SettingsService object.
 * @remarks If storage is not localStorage and roaming is not Office.context.roamingSettings, a new
 * SettingsService object is created and not cached. If the storage and roaming settings are the
 * defaults, the object will be cached and returned as needed.
 */
export function getSettingsService(
  name: 'bombbomb',
  storage?: Storage,
  roaming?: Office.RoamingSettings,
): AppSettingsService;
export function getSettingsService(
  name: 'sendingPool',
  storage?: Storage,
  roaming?: Office.RoamingSettings,
): SendingPoolSettingsService;
export function getSettingsService(
  name: 'videoStats',
  storage?: Storage,
  roaming?: Office.RoamingSettings,
): VideoStatsSettingsService;
export function getSettingsService(
  name: 'ui',
  storage?: Storage,
  roaming?: Office.RoamingSettings,
): UISettingsService;
export function getSettingsService(
  name: keyof typeof services,
  storage: Storage = localStorage,
  roaming: Office.RoamingSettings = Office.context.roamingSettings,
):
  | AppSettingsService
  | SendingPoolSettingsService
  | VideoStatsSettingsService
  | UISettingsService
  | undefined {
  const cacheEntry = Object.is(storage, localStorage) && Object.is(roaming, Office.context.roamingSettings);

  if (name in services && cacheEntry) {
    return services[name];
  }

  if (name === 'bombbomb') {
    const service = new SettingsService<AppSettings>(
      'bombbomb',
      localStorage,
      Office.context.roamingSettings,
    );
    if (cacheEntry) {
      services[name] = service;
    }
    return service;
  }

  if (name === 'sendingPool') {
    const service = new SettingsService<SendingPoolSettings>(
      'sendingPool',
      localStorage,
      Office.context.roamingSettings,
    );
    if (cacheEntry) {
      services[name] = service;
    }
    return service;
  }

  if (name === 'videoStats') {
    const service = new SettingsService<VideoStatsSettings>(
      'videoStats',
      localStorage,
      Office.context.roamingSettings,
    );
    if (cacheEntry) {
      services[name] = service;
    }
    return service;
  }

  if (name === 'ui') {
    const service = new SettingsService<UISettings>('ui', localStorage, Office.context.roamingSettings);
    if (cacheEntry) {
      services[name] = service;
    }
    return service;
  }

  return undefined;
}

/**
 * Manages settings in local storage & roaming settings.
 */
export class SettingsService<T extends BaseSettings> extends EventEmitter {
  protected data!: Nullable<T>;
  protected debug: Debug.Debugger;
  protected initOverride!: Required<SettingsSetup<T>>['initOverride'];

  constructor(
    protected name: keyof typeof services,
    protected storage: Storage,
    protected roamingStorage: Office.RoamingSettings | null = null,
  ) {
    super();
    this.debug = Debug(`o365:SettingsService:${name}`);

    this.loadInitialState();
    this.readSettings();
  }

  /**
   * Removes any currently set settings from both local and remote storage, persists it,
   * and reloads from a blank state via @see getSetupData.
   */
  clear() {
    this.storage.removeItem(this.name);
    this.roamingStorage?.remove(this.name);
    localStorage.removeItem(this.name);
    this.saveSettings();
    this.loadInitialState();
  }

  /**
   * Retrieve a setting from our data
   */
  get<K extends keyof T>(key: K): Nullable<T>[K] {
    if (key in this.data) {
      return this.data[key];
    }

    return null;
  }

  /**
   * Return all the data
   */
  getAll(): Nullable<T> {
    return this.data;
  }

  getSettingsForSave() {
    // create the settings by serializing any objects
    const saveSettings = Object.entries(this.data).reduce((data, currentEntry) => {
      const [key, rawValue] = currentEntry;
      if (rawValue && typeof rawValue.serialize === 'function') {
        data[key] = rawValue.serialize();
      } else {
        data[key] = rawValue;
      }
      return data;
    }, {} as Record<string, unknown>);

    return saveSettings;
  }

  /**
   * Create a correct/expected value based on key.
   *
   * In some cases, we store a class instance in settings and this will restore those correctly.
   */
  initializeFromStorage<K extends keyof T, V extends T[K]>(key: K, value: V) {
    if (value === null && this.data[key] !== null) {
      this.debug('skipped overriding current value with null', { key, value });
      return;
    }

    if (this.initOverride(this.data, key, value, this.debug)) {
      return;
    }

    this.data[key] = value;
  }

  /** Loads the initial state of the data and init based on @see getSetupData. */
  loadInitialState() {
    const setup = getSetupData(this.name) as SettingsSetup<T>;

    if (setup && setup.initialData) {
      this.data = { ...setup.initialData };
    } else {
      this.data = {} as Nullable<T>;
    }

    this.initOverride = setup?.initOverride ?? (() => false);
  }

  /**
   * Get specficially requested properties from the data object.
   */
  pick<K extends keyof T>(...services: K[]): Nullable<Pick<T, K>> {
    return services.reduce((obj, key) => {
      obj[key] = this.data[key];
      return obj;
    }, {} as Nullable<Pick<T, K>>);
  }

  /**
   * Read settings from local storage...
   */
  readLocalSettings() {
    this.debug('readLocalSettings');
    const data = this.storage.getItem(this.name);
    if (data) {
      try {
        const parsedData = JSON.parse(data);
        if (parsedData.timestamp && this.data.timestamp && this.data.timestamp > parsedData.timestamp) {
          this.debug('skipped local settings read: current data is newer');
          return;
        }

        if (parsedData.environment !== environment) {
          this.debug('skipped local settings readings: environment mismatch');
          return;
        }

        Object.keys(this.data).forEach((key) => {
          this.initializeFromStorage(key as keyof T, parsedData[key]);
        });
      } catch (err) {
        this.debug('could not read storage data', err);
      }
    } else {
      this.debug('no local settings to read');
    }
  }

  /**
   * Read settings from roaming storage...
   */
  readRoamingSettings() {
    this.debug('readRoamingSettings');
    if (!this.roamingStorage) {
      return;
    }

    const data = this.roamingStorage.get(this.name);
    if (data) {
      try {
        const parsedData = JSON.parse(data);

        // we have a timestamp that lives with the settings so we can determine if data is newer or not
        if (parsedData.timestamp && this.data.timestamp && this.data.timestamp >= parsedData.timestamp) {
          this.debug('skipped remote settings read: current data is newer or the same');
          return;
        } else {
          this.debug('timestamps', parsedData.timestamp, this.data.timestamp);
        }
        if (parsedData.environment !== environment) {
          this.debug('skipped remote settings readings: environment mismatch');
          return;
        }

        Object.keys(this.data).forEach((key) => {
          this.initializeFromStorage(key as keyof T, parsedData[key]);
        });
        this.debug('remote settings read');
      } catch (err) {
        this.debug('could not read roaming settings', err);
      }
    } else {
      this.debug('no remote settings to read');
    }
  }

  /**
   * Read local & then roaming settings
   */
  readSettings() {
    this.emit('readSettings:start');
    this.readLocalSettings();
    this.readRoamingSettings();
    this.data.timestamp = Date.now();
    this.emit('readSettings:done');
  }

  /**
   * Save settings to both locations.
   */
  async saveSettings() {
    this.debug('saveSettings', { ...this.data });
    const saveSettings = this.getSettingsForSave();
    this.storage.setItem(this.name, JSON.stringify(saveSettings));
    this.debug('local settings saved', saveSettings);
    const save = await this.saveRemoteSettings();
    if (save) {
      this.emit('saved', saveSettings);
    }
    return save;
  }

  /**
   * Saving roaming settings is a bit more delicate...
   */
  saveRemoteSettings(): Promise<boolean> {
    return new Promise((resolve) => {
      const saveSettings = this.getSettingsForSave();
      if (this.roamingStorage) {
        const current = this.roamingStorage.get(this.name);
        if (current) {
          try {
            const parsedData = JSON.parse(current);
            const remoteIsSameEnvironment = parsedData.environment === environment;
            const remoteHasNewerSettings =
              parsedData.timestamp && (saveSettings.timestamp as number) <= parsedData.timestamp;
            if (remoteIsSameEnvironment && remoteHasNewerSettings) {
              this.debug('skipping remote settings save as remote has newer settings');
              return;
            }
          } catch (err) {
            this.debug('error parsing roaming settings to see if we can override');
          }
        }
        this.roamingStorage.set(this.name, JSON.stringify(saveSettings));
        try {
          this.roamingStorage.saveAsync((result) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
              this.debug('Could not save remote settings');
              return resolve(false);
            }
            this.debug('Remote settings saved', saveSettings);
            return resolve(true);
          });
        } catch (err) {
          this.debug('Could not save remote settings', err);
          console.error(err);
          return resolve(false);
        }
      } else {
        this.debug('could not save remote settings: roaming settings not available?');
        return resolve(false);
      }
    });
  }

  /**
   * Set a setting's value
   */
  set<K extends keyof T, V extends T[K]>(key: K, value: V, save = true) {
    this.data[key] = value;
    this.data.timestamp = Date.now();
    if (save) {
      return this.saveSettings();
    }
    this.emit(`set:${String(key)}`, value);
    return Promise.resolve();
  }

  /**
   * Set multiple setting values at once (and save once done)
   */
  setBatch(settings: Partial<Nullable<T>>, save = true) {
    Object.entries(settings).forEach(([key, value]) => {
      this.data[key as keyof T] = value;
    });

    this.data.timestamp = Date.now();
    if (save) {
      this.saveSettings();
    }
  }
}

type SettingsSetupBase = {
  initialData?: unknown;
  // We want a generic function here to be able to stack with SettingsSetup<T>.
  // eslint-disable-next-line @typescript-eslint/ban-types
  initOverride?: Function;
};

type SettingsSetup<T> = SettingsSetupBase & {
  /** Describes the initial data the settings will use before initialization. */
  initialData?: Nullable<T>;

  /**
   * Provides an opportunity to override the initialization process for your custom type.
   * @arg data: The current data object being initialized.
   * @arg key: The key of the data being loaded.
   * @arg value: The value of data being loaded.
   * @arg debug: Debugger method used to output diagnostic methods.
   * @returns Return true to indicate the initialization has been handled and that the default
   * action should not occur. Return false to indicate that the default action should occur.
   */
  initOverride?: <K extends keyof T, V extends T[K]>(
    data: Nullable<T>,
    key: K,
    value: V,
    debug: Debug.Debugger,
  ) => boolean;
};

/**
 * Gets customized setup data for the SettingsService to use, based on the name of the settings.
 * @arg name: The name of the settings to get customized data for.
 * @returns A setup data object for the setting type or nothing, if no customization is needed.
 */
function getSetupData(name: keyof typeof services): SettingsSetupBase | undefined {
  if (name === 'bombbomb') {
    return {
      initialData: {
        bixelSendingPool: null,
        environment,
        sessionId: null,
        token: null,
        trial: null,
        user: null,
        version: null, // TODO: we need to figure out how to get this in React
        timestamp: null,
        livefireFilters: null,
        trackAll: trackByDefault(),
        filterTracking: true,
        loginState: 0,
      },
      initOverride: (data, key, value, debug) => {
        if (key === 'user') {
          let stale = !!value;
          if (typeof value === 'object' && 'timestamp' in value) {
            stale = value.timestamp + 60 * 1000 * 60 < Date.now();
          }

          if (!stale && value !== null) {
            data[key] = new User(value);
            return true;
          }

          debug('skipped stale user data');
          data[key] = null;
          return true;
        }

        if (key === 'token') {
          if (value && value.expiry < Date.now()) {
            data[key] = null;
            debug('skipped expired token');
            return true;
          }

          data[key] = createTokenFromSerializedToken(value);
          return true;
        }

        return false;
      },
    } as SettingsSetup<AppSettings>;
  }

  if (name === 'sendingPool') {
    return {
      initialData: {
        environment,
        host: null,
        hostUrl: null,
        timestamp: null,
      },
    } as SettingsSetup<SendingPoolSettings>;
  }

  if (name === 'videoStats') {
    return {
      initialData: {
        environment,
        hasShownSendAndTrackInfo: false,
        hasDismissedSendAndTrackInfo: false,
      },
    } as SettingsSetup<VideoStatsSettings>;
  }

  if (name === 'ui') {
    return {
      initialData: {
        environment,
        showTrialBannerAfter: 0,
        dialog: null,
        videoTitles: null,
        videoIds: null,
      },
    } as SettingsSetup<UISettings>;
  }

  return undefined;
}
